import React, { useEffect, useMemo, useRef, useState } from "react";
import { AdminNav } from "@/components/AdminNav";
import { previewFrameName } from "@/lib/previewLanguage";
import { AUDIT_LANGS, auditSources } from "@/lib/l10n/audit";
import { appSources } from "@/lib/l10n/registry";

const DEVICE_PRESETS = [
  { id: "mobile-360", label: "Mobile · 360 × 800", width: 360, height: 800 },
  { id: "mobile-390", label: "Mobile · 390 × 844", width: 390, height: 844 },
  { id: "mobile-430", label: "Large Mobile · 430 × 932", width: 430, height: 932 },
  { id: "tablet-768", label: "Tablet Portrait · 768 × 1024", width: 768, height: 1024 },
  { id: "tablet-1024", label: "Tablet Landscape · 1024 × 768", width: 1024, height: 768 },
  { id: "laptop", label: "Laptop · 1366 × 768", width: 1366, height: 768 },
  { id: "desktop", label: "Desktop · 1920 × 1080", width: 1920, height: 1080 },
  { id: "custom", label: "Custom", width: 390, height: 844 },
];

const SCREEN_PRESETS = [
  { id: "setup", label: "Interview Session Setup", path: "/upload" },
  { id: "account", label: "Account Settings", path: "/account" },
  { id: "downloads", label: "Reports & Downloads", path: "/downloads" },
  { id: "admin", label: "Admin Dashboard", path: "/admin" },
  { id: "prepare", label: "Preparation · Session ID required", path: "/prepare/:sessionId", needsSession: true },
  { id: "interview", label: "Warm-up / Interview · Session ID required", path: "/interview/:sessionId", needsSession: true },
  { id: "report", label: "Report · Session ID required", path: "/report/:sessionId", needsSession: true },
  { id: "custom", label: "Custom app path", path: "", custom: true },
];

// Language (localization audit, Part 2, 2026-11-03): the preview shows the app, and
// the report or interview loaded in it, in this language without changing the account.
const PREVIEW_LANG_OPTIONS = [
  { value: "", label: "Account language" },
  { value: "en", label: "English" },
  { value: "pt-BR", label: "Portuguese (pt-BR)" },
  { value: "es-419", label: "Spanish (es-419)" },
  { value: "fr-CA", label: "French (fr-CA)" },
];

// The Localization Audit's screens that each preset shows ("Report" also covers its tips).
const AUDIT_SCREENS_FOR = {
  setup: ["Interview Session Setup", "Practice format", "Voice choice", "Interview already in progress", "Sessions used"],
  account: ["Account"],
  downloads: ["My Reports"],
  prepare: ["Preparing"],
  interview: ["Interview (", "Interview –", "Sessions used", "Help – button"],
  report: ["Report", "Free trial"],
};

const clamp = (value, min, max) => Math.min(max, Math.max(min, Number(value) || min));

function appendPreviewFlag(path) {
  const join = path.includes("?") ? "&" : "?";
  return `${path}${join}qaPreview=1`;
}

function supportsCredentiallessIframes() {
  return (
    typeof window !== "undefined" &&
    typeof window.HTMLIFrameElement !== "undefined" &&
    "credentialless" in window.HTMLIFrameElement.prototype
  );
}

export default function AdminResponsivePreview() {
  const stageRef = useRef(null);
  const iframeRef = useRef(null);
  const [deviceId, setDeviceId] = useState("tablet-768");
  const initialDevice = DEVICE_PRESETS.find((d) => d.id === "tablet-768");
  const [width, setWidth] = useState(initialDevice.width);
  const [height, setHeight] = useState(initialDevice.height);
  const [screenId, setScreenId] = useState("setup");
  const [sessionId, setSessionId] = useState("");
  const [customPath, setCustomPath] = useState("/upload");
  const [scaleMode, setScaleMode] = useState("fit");
  const [manualScale, setManualScale] = useState(75);
  const [availableWidth, setAvailableWidth] = useState(1200);
  const [interactionEnabled, setInteractionEnabled] = useState(false);
  const [previewLang, setPreviewLang] = useState(""); // "" = the account's own language
  const [reloadKey, setReloadKey] = useState(0);
  const credentiallessSupported = supportsCredentiallessIframes();

  useEffect(() => {
    const el = stageRef.current;
    if (!el) return undefined;

    const measure = () => setAvailableWidth(Math.max(320, el.clientWidth));
    measure();
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    if (observer) observer.observe(el);
    window.addEventListener("resize", measure);
    return () => {
      if (observer) observer.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);

  const selectedScreen = SCREEN_PRESETS.find((screen) => screen.id === screenId) || SCREEN_PRESETS[0];
  // Texts on this screen much longer than the English (Localization Audit "May not fit").
  const auditRows = useMemo(() => auditSources(appSources()), []);
  const mayNotFit = useMemo(() => {
    if (!AUDIT_LANGS.includes(previewLang)) return [];
    const screens = AUDIT_SCREENS_FOR[screenId];
    return auditRows.filter((r) => (r.problems[previewLang] || []).includes("long")
      && (!screens || screens.some((name) => r.screen.startsWith(name))));
  }, [auditRows, previewLang, screenId]);

  const rawPath = useMemo(() => {
    if (selectedScreen.custom) {
      const trimmed = customPath.trim();
      if (!trimmed.startsWith("/") || trimmed.startsWith("//")) return null;
      if (trimmed.startsWith("/admin/responsive-preview")) return null;
      return trimmed;
    }
    if (selectedScreen.needsSession) {
      const cleanId = sessionId.trim();
      if (!cleanId) return null;
      return selectedScreen.path.replace(":sessionId", encodeURIComponent(cleanId));
    }
    return selectedScreen.path;
  }, [selectedScreen, customPath, sessionId]);

  const previewSrc = rawPath ? appendPreviewFlag(rawPath) : null;
  const fitScale = Math.min(1, Math.max(0.2, (availableWidth - 48) / width));
  const scale = scaleMode === "fit" ? fitScale : clamp(manualScale, 25, 125) / 100;
  const scaledWidth = Math.round(width * scale);
  const scaledHeight = Math.round(height * scale);

  const selectDevice = (id) => {
    setDeviceId(id);
    const preset = DEVICE_PRESETS.find((device) => device.id === id);
    if (preset && id !== "custom") {
      setWidth(preset.width);
      setHeight(preset.height);
    }
  };

  const setDimension = (dimension, value) => {
    setDeviceId("custom");
    if (dimension === "width") setWidth(clamp(value, 280, 2560));
    else setHeight(clamp(value, 480, 1600));
  };

  const rotate = () => {
    setDeviceId("custom");
    setWidth(height);
    setHeight(width);
  };

  const openPreview = () => {
    if (!previewSrc || interactionEnabled) return;
    window.open(previewSrc, previewFrameName(previewLang) || "_blank", "noopener,noreferrer");
  };

  const toggleInteraction = (enabled) => {
    if (enabled && !credentiallessSupported) return;
    setInteractionEnabled(enabled);
  };

  return (
    <div className="min-h-screen w-full bg-[color:var(--bg-base)]" data-testid="admin-responsive-preview">
      <AdminNav title="Responsive Preview Lab" />

      <main className="max-w-[1600px] mx-auto p-4 sm:p-6 space-y-5">
        <section className="rounded-2xl border border-[color:var(--border-stone)] bg-white p-4 sm:p-5">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-end">
            <label className="flex min-w-[220px] flex-1 flex-col gap-1.5 text-xs font-medium text-[color:var(--text-primary)]">
              Screen
              <select
                value={screenId}
                onChange={(event) => setScreenId(event.target.value)}
                className="h-10 rounded-xl border border-[color:var(--border-stone)] bg-white px-3 text-sm"
                data-testid="responsive-screen-select"
              >
                {SCREEN_PRESETS.map((screen) => (
                  <option key={screen.id} value={screen.id}>{screen.label}</option>
                ))}
              </select>
            </label>

            <label className="flex min-w-[180px] flex-col gap-1.5 text-xs font-medium text-[color:var(--text-primary)]">
              Language
              <select
                value={previewLang}
                onChange={(event) => setPreviewLang(event.target.value)}
                className="h-10 rounded-xl border border-[color:var(--border-stone)] bg-white px-3 text-sm"
                data-testid="responsive-language-select"
              >
                {PREVIEW_LANG_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </label>

            <label className="flex min-w-[220px] flex-1 flex-col gap-1.5 text-xs font-medium text-[color:var(--text-primary)]">
              Device preset
              <select
                value={deviceId}
                onChange={(event) => selectDevice(event.target.value)}
                className="h-10 rounded-xl border border-[color:var(--border-stone)] bg-white px-3 text-sm"
                data-testid="responsive-device-select"
              >
                {DEVICE_PRESETS.map((device) => (
                  <option key={device.id} value={device.id}>{device.label}</option>
                ))}
              </select>
            </label>

            <div className="flex flex-wrap items-end gap-2">
              <label className="flex flex-col gap-1.5 text-xs font-medium text-[color:var(--text-primary)]">
                Width
                <input
                  type="number"
                  min="280"
                  max="2560"
                  value={width}
                  onChange={(event) => setDimension("width", event.target.value)}
                  className="h-10 w-24 rounded-xl border border-[color:var(--border-stone)] px-3 text-sm"
                  data-testid="responsive-width"
                />
              </label>
              <label className="flex flex-col gap-1.5 text-xs font-medium text-[color:var(--text-primary)]">
                Height
                <input
                  type="number"
                  min="480"
                  max="1600"
                  value={height}
                  onChange={(event) => setDimension("height", event.target.value)}
                  className="h-10 w-24 rounded-xl border border-[color:var(--border-stone)] px-3 text-sm"
                  data-testid="responsive-height"
                />
              </label>
              <button
                type="button"
                onClick={rotate}
                className="h-10 rounded-full border border-[color:var(--border-stone)] px-4 text-sm font-medium hover:bg-[color:var(--bg-surface-hover)]"
                data-testid="responsive-rotate"
              >
                Rotate
              </button>
            </div>

            <label className="flex min-w-[120px] flex-col gap-1.5 text-xs font-medium text-[color:var(--text-primary)]">
              Scale
              <select
                value={scaleMode}
                onChange={(event) => setScaleMode(event.target.value)}
                className="h-10 rounded-xl border border-[color:var(--border-stone)] bg-white px-3 text-sm"
                data-testid="responsive-scale-mode"
              >
                <option value="fit">Fit to workspace</option>
                <option value="manual">Manual</option>
              </select>
            </label>

            {scaleMode === "manual" && (
              <label className="flex flex-col gap-1.5 text-xs font-medium text-[color:var(--text-primary)]">
                Zoom %
                <input
                  type="number"
                  min="25"
                  max="125"
                  value={manualScale}
                  onChange={(event) => setManualScale(clamp(event.target.value, 25, 125))}
                  className="h-10 w-24 rounded-xl border border-[color:var(--border-stone)] px-3 text-sm"
                  data-testid="responsive-manual-scale"
                />
              </label>
            )}
          </div>

          {selectedScreen.needsSession && (
            <div className="mt-4">
              <label className="flex max-w-2xl flex-col gap-1.5 text-xs font-medium text-[color:var(--text-primary)]">
                Session ID
                <input
                  type="text"
                  value={sessionId}
                  onChange={(event) => setSessionId(event.target.value)}
                  placeholder="Paste a staging session ID"
                  className="h-10 rounded-xl border border-[color:var(--border-stone)] px-3 text-sm font-mono"
                  data-testid="responsive-session-id"
                />
              </label>
              <p className="mt-1.5 text-[11px] text-[color:var(--text-secondary)]">
                Use a staging test session. Preview mode suppresses browser-exit cleanup while the page is inside this lab.
              </p>
            </div>
          )}

          {selectedScreen.custom && (
            <div className="mt-4">
              <label className="flex max-w-2xl flex-col gap-1.5 text-xs font-medium text-[color:var(--text-primary)]">
                Local app path
                <input
                  type="text"
                  value={customPath}
                  onChange={(event) => setCustomPath(event.target.value)}
                  placeholder="/some-app-route"
                  className="h-10 rounded-xl border border-[color:var(--border-stone)] px-3 text-sm font-mono"
                  data-testid="responsive-custom-path"
                />
              </label>
              <p className="mt-1.5 text-[11px] text-[color:var(--text-secondary)]">
                Local paths only. The Preview Lab itself cannot be nested recursively.
              </p>
            </div>
          )}

          <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-[color:var(--border-stone)] pt-4">
            <button
              type="button"
              onClick={() => setReloadKey((key) => key + 1)}
              disabled={!previewSrc}
              className="h-10 rounded-full bg-[color:var(--brand)] px-5 text-sm font-medium text-white hover:bg-[color:var(--brand-hover)] disabled:opacity-40"
              data-testid="responsive-reload"
            >
              Reload preview
            </button>
            <button
              type="button"
              onClick={openPreview}
              disabled={!previewSrc || interactionEnabled}
              title={interactionEnabled ? "Full-window preview is disabled while the isolated test-user session is active." : undefined}
              className="h-10 rounded-full border border-[color:var(--border-stone)] px-4 text-sm font-medium hover:bg-[color:var(--bg-surface-hover)] disabled:cursor-not-allowed disabled:opacity-40"
              data-testid="responsive-open-window"
            >
              Open at full browser size
            </button>
            <label className="ml-auto inline-flex items-center gap-2 text-xs font-medium text-[color:var(--text-primary)]">
              <input
                type="checkbox"
                checked={interactionEnabled}
                disabled={!credentiallessSupported}
                onChange={(event) => toggleInteraction(event.target.checked)}
                className="h-4 w-4 accent-[color:var(--brand)] disabled:cursor-not-allowed disabled:opacity-50"
                data-testid="responsive-interaction-toggle"
              />
              Enable isolated interaction
            </label>
          </div>

          {interactionEnabled && (
            <p className="mt-3 text-xs leading-relaxed text-[color:var(--text-secondary)]" data-testid="responsive-isolation-note">
              Interactive mode uses an isolated browser session. You can sign in inside the preview with a test account without replacing your Admin login outside the frame. Browser password autofill and Google sign-in are intentionally unavailable in this isolated mode.
            </p>
          )}
          {!credentiallessSupported && (
            <p className="mt-3 text-xs leading-relaxed text-amber-700" data-testid="responsive-isolation-unsupported">
              Isolated interaction is not supported by this browser. Use a current Chromium-based browser for test-account interaction; inspection mode remains available.
            </p>
          )}
        </section>

        {mayNotFit.length > 0 && (
          <details className="rounded-2xl border border-[color:var(--border-stone)] bg-white p-4 sm:p-5 text-sm" data-testid="responsive-may-not-fit">
            <summary className="cursor-pointer font-medium">
              {mayNotFit.length} texts on this screen may not fit in {previewLang} (much longer than the English)
            </summary>
            <ul className="mt-3 space-y-2 text-xs">
              {mayNotFit.map((r) => (
                <li key={`${r.source}|${r.key}`} className="border-t border-[color:var(--border-stone)] pt-2">
                  <div className="text-[10px] text-[color:var(--text-secondary)]">{r.screen} · {r.key}</div>
                  <div><span className="text-[color:var(--text-secondary)]">en:</span> {r.en}</div>
                  <div><span className="text-[color:var(--text-secondary)]">{previewLang}:</span> {r[previewLang]}</div>
                </li>
              ))}
            </ul>
          </details>
        )}

        <section className="rounded-2xl border border-[color:var(--border-stone)] bg-white overflow-hidden">
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 border-b border-[color:var(--border-stone)] px-4 py-3 text-xs">
            <span className="font-semibold text-[color:var(--text-primary)]">Preview viewport</span>
            <span className="font-mono text-[color:var(--text-secondary)]">{width} × {height}px</span>
            <span className="text-[color:var(--text-secondary)]">Displayed at {Math.round(scale * 100)}%</span>
            <span className={`rounded-full px-2.5 py-1 text-[11px] font-medium ${interactionEnabled ? "bg-amber-50 text-amber-800" : "bg-emerald-50 text-emerald-800"}`}>
              {interactionEnabled ? "Interactive · isolated" : "Inspection only"}
            </span>
            {rawPath && <span className="ml-auto font-mono text-[11px] text-[color:var(--text-secondary)]">{rawPath}</span>}
          </div>

          <div
            ref={stageRef}
            className="min-h-[720px] overflow-auto bg-[#eceff1] p-6"
            data-testid="responsive-preview-stage"
          >
            {!previewSrc ? (
              <div className="mx-auto mt-16 max-w-lg rounded-2xl border border-dashed border-[color:var(--border-stone)] bg-white p-8 text-center">
                <div className="text-lg font-semibold text-[color:var(--text-primary)]">Preview route is not ready</div>
                <p className="mt-2 text-sm leading-relaxed text-[color:var(--text-secondary)]">
                  {selectedScreen.needsSession
                    ? "Enter a staging session ID above to preview this screen."
                    : "Enter a valid local app path beginning with /."}
                </p>
              </div>
            ) : (
              <div
                className="mx-auto"
                style={{ width: scaledWidth, height: scaledHeight }}
                data-testid="responsive-preview-frame-shell"
              >
                <div
                  style={{
                    width,
                    height,
                    transform: `scale(${scale})`,
                    transformOrigin: "top left",
                  }}
                >
                  <iframe
                    key={`${previewSrc}-${interactionEnabled ? "isolated" : "shared"}-${previewLang}-${reloadKey}`}
                    ref={iframeRef}
                    name={previewFrameName(previewLang)}
                    src={previewSrc}
                    title={`Responsive preview of ${selectedScreen.label}`}
                    {...(interactionEnabled ? { credentialless: "" } : {})}
                    style={{
                      width,
                      height,
                      border: "1px solid #cfd5d8",
                      background: "white",
                      pointerEvents: interactionEnabled ? "auto" : "none",
                    }}
                    data-testid="responsive-preview-iframe"
                  />
                </div>
              </div>
            )}
          </div>
        </section>

        <section className="grid gap-4 lg:grid-cols-2">
          <div className="rounded-2xl border border-[color:var(--border-stone)] bg-white p-5">
            <h2 className="text-sm font-semibold text-[color:var(--text-primary)]">What this lab validates</h2>
            <p className="mt-2 text-sm leading-relaxed text-[color:var(--text-secondary)]">
              Responsive breakpoints, header alignment, wrapping, spacing, button placement, page overflow, dropdown positioning and the actual screen layout at specific CSS viewport dimensions.
            </p>
          </div>
          <div className="rounded-2xl border border-[color:var(--border-stone)] bg-white p-5">
            <h2 className="text-sm font-semibold text-[color:var(--text-primary)]">Still verify on real devices before release</h2>
            <p className="mt-2 text-sm leading-relaxed text-[color:var(--text-secondary)]">
              Mobile Safari/Chrome browser chrome, touch ergonomics, safe-area insets, on-screen keyboards, microphone permissions and hardware audio behavior cannot be perfectly reproduced by a desktop viewport frame.
            </p>
          </div>
        </section>
      </main>
    </div>
  );
}
